// Schéma de série des méthodes composées — validation du JSON, puis résolution des
// charges palier par palier.
//
// Deux étages volontairement séparés :
//   — `parseSetScheme` ne regarde que la FORME. Il tourne au chargement du programme,
//     sans rien connaître de l'athlète, et renvoie null sur tout contenu douteux plutôt
//     que d'afficher un schéma faux (même contrat que `parseTestProtocol`).
//   — `resolveSetScheme` calcule les CHARGES, ce qui demande le 1RM, le poids de corps
//     et le niveau. Il ne tourne qu'à l'écran, quand ces données sont connues.
//
// Le contenu, lui, vient du JSON du programme : ajuster un protocole se fait là, pas
// ici. Voir `scripts/migrate-set-schemes.js` pour la façon dont il y a été écrit.

import type { Exercise } from "@/types/programs"
import type {
  PalierCharge,
  PalierTransition,
  SetPalier,
  SetScheme,
  SetSchemeMethod,
} from "@/types/session"
import {
  computeChargeKg,
  resolveEquipment,
  roundPalierSeries,
  type AthleteLevel,
  type AthleteOneRm,
} from "@/lib/exerciseNormalization"

/** Le schéma tel qu'il est écrit dans le JSON, une fois validé. */
export type ProgramSetScheme = NonNullable<Exercise["set_scheme"]>

const METHODS: readonly SetSchemeMethod[] = [
  "rest-pause", "drop-set", "pyramide-montante", "bi-set", "cluster", "myo-reps",
]

function parseTransition(raw: unknown): PalierTransition | null | "invalid" {
  if (raw == null) return null
  if (typeof raw !== "object") return "invalid"
  const o = raw as Record<string, unknown>
  if (o.kind === "chain") return { kind: "chain" }
  if (o.kind === "load-change") return { kind: "load-change", deltaKg: null }
  if (o.kind === "pause") {
    const seconds = Number(o.seconds)
    // Une pause de zéro seconde n'est pas une pause — c'est un enchaînement, et le
    // JSON aurait dû l'écrire ainsi. On refuse plutôt que de deviner.
    if (!Number.isFinite(seconds) || seconds <= 0) return "invalid"
    return { kind: "pause", seconds, ...(o.estimated === true ? { estimated: true } : {}) }
  }
  return "invalid"
}

/**
 * Valide la forme du `set_scheme` d'un exercice. Renvoie null si le champ est absent
 * ou mal formé — l'écran retombe alors sur l'affichage en séries droites, qui reste
 * juste, plutôt que sur un schéma partiel qui ne le serait pas.
 */
export function parseSetScheme(raw: unknown): ProgramSetScheme | null {
  if (typeof raw !== "object" || raw == null) return null
  const o = raw as Record<string, unknown>

  const method = o.method as SetSchemeMethod
  if (!METHODS.includes(method)) return null
  const label = typeof o.label === "string" ? o.label.trim() : ""
  if (!label) return null
  // Un seul palier n'est pas une méthode composée : il n'y a alors aucune transition à
  // montrer, et les tuiles Séries/Reps/Charge disent déjà tout.
  if (!Array.isArray(o.paliers) || o.paliers.length < 2) return null

  const paliers: ProgramSetScheme["paliers"] = []
  for (const [i, item] of o.paliers.entries()) {
    if (typeof item !== "object" || item == null) return null
    const it = item as Record<string, unknown>
    const reps = typeof it.reps === "string" ? it.reps.trim() : ""
    const load = typeof it.load === "string" ? it.load.trim() : ""
    if (!reps || !load) return null

    const isLast = i === o.paliers.length - 1
    const transition = parseTransition(it.transition)
    if (transition === "invalid") return null
    // La transition est ce qui désigne le dernier palier : il n'en a pas, les autres en
    // ont une. Un JSON qui s'écarte de ça décrit une série qu'on ne sait pas dérouler.
    if (isLast !== (transition == null)) return null

    paliers.push({
      reps,
      load,
      ...(typeof it.movement === "string" && it.movement.trim() ? { movement: it.movement.trim() } : {}),
      ...(transition ? { transition: transition as ProgramSetScheme["paliers"][number]["transition"] } : {}),
    })
  }
  return { method, label, paliers }
}

/**
 * Charge affichable d'un palier — même grammaire, et même code, que la tuile Charge de
 * l'exercice. Un palier de bi-set porte son propre mouvement, et donc son propre ratio.
 */
function palierCharge(
  movementName: string,
  load: string,
  oneRm: AthleteOneRm | null,
  bodyweightKg: number | null,
  level: AthleteLevel
): PalierCharge {
  const resolved = computeChargeKg(movementName, load, oneRm, bodyweightKg, level)
  const m = resolved.movements[0]
  const pct = /(\d+(?:[.,]\d+)?)\s*%/.exec(m.detail ?? "")
  return {
    label: m.weight,
    kg: m.weightKg,
    percent1Rm: pct ? parseFloat(pct[1].replace(",", ".")) : null,
    basis: m.basis,
    ...(m.perDumbbell ? { perDumbbell: true } : {}),
  }
}

/**
 * Résout un schéma validé en paliers affichables : charges converties en kg, arrondies
 * au cran de matériel sur l'ENSEMBLE de la série (voir `roundPalierSeries` — c'est ce
 * qui garantit l'écart d'un cran entre deux paliers voisins), puis écarts de charge
 * calculés pour les transitions `load-change`.
 *
 * L'arrondi ne s'applique qu'aux paliers qui partagent un même mouvement : un bi-set
 * enchaîne deux exercices différents, sur deux matériels et deux 1RM sans rapport, et
 * leurs charges n'ont aucune raison d'être distinctes l'une de l'autre.
 */
export function resolveSetScheme(
  scheme: ProgramSetScheme,
  exerciseName: string,
  oneRm: AthleteOneRm | null,
  bodyweightKg: number | null,
  level: AthleteLevel
): SetScheme {
  const charges = scheme.paliers.map(p =>
    palierCharge(p.movement ?? exerciseName, p.load, oneRm, bodyweightKg, level)
  )

  const sameMovement = scheme.paliers.every(p => !p.movement)
  if (sameMovement) {
    const equipment = resolveEquipment(exerciseName)
    // Le 1RM se relit depuis n'importe quel palier converti : kg = 1RM × pct/100.
    const ref = charges.find(c => c.kg != null && c.percent1Rm != null && c.percent1Rm > 0)
    if (equipment && ref) {
      const oneRmKg = (ref.kg! * 100) / ref.percent1Rm!
      const rounded = roundPalierSeries(charges.map(c => c.percent1Rm), oneRmKg, equipment)
      rounded.forEach((kg, i) => {
        if (kg == null || charges[i].kg == null) return
        charges[i] = { ...charges[i], kg, label: formatKg(kg, charges[i]) }
      })
    }
  }

  const paliers: SetPalier[] = scheme.paliers.map((p, i) => {
    const transition = p.transition
      ? resolveTransition(p.transition, charges[i], charges[i + 1])
      : undefined
    return {
      index: i + 1,
      reps: p.reps,
      charge: charges[i],
      ...(p.movement ? { movement: p.movement } : {}),
      ...(transition ? { transition } : {}),
    }
  })

  return { method: scheme.method, label: scheme.label, paliers }
}

function resolveTransition(
  raw: NonNullable<ProgramSetScheme["paliers"][number]["transition"]>,
  from: PalierCharge,
  to: PalierCharge | undefined
): PalierTransition {
  if (raw.kind !== "load-change") return raw
  const deltaKg =
    from.kg != null && to?.kg != null ? Math.round((to.kg - from.kg) * 10) / 10 : null
  return { kind: "load-change", deltaKg }
}

/**
 * Réécrit le libellé après arrondi. On ne reformate que les charges externes en kg :
 * un "Poids du corps", un lest ou une assistance ont leur propre phrasé, posé par
 * `computeChargeKg`, et l'arrondi de série ne les concerne pas.
 */
function formatKg(kg: number, charge: PalierCharge): string {
  if (charge.basis !== "external") return charge.label
  const prefix = charge.label.startsWith("~") ? "~" : ""
  return charge.perDumbbell ? `${prefix}${kg} kg par haltère` : `${prefix}${kg} kg`
}
